/**
 * Timezone Hook
 *
 * Manages timezone fetching, auto-detection, and updates.
 * Integrates with the timezone store and API.
 */

import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/client/lib/api';
import {
  useTimezoneStore,
  getEffectiveTimezone,
  getBrowserTimezone,
  getTimezoneOffset,
} from '@/client/stores/timezone-store';
import { useAuth } from './use-auth';

interface TimezoneResponse {
  timezone: string | null;
}

interface TimezoneDetectResponse {
  timezone: string | null;
  detected: boolean;
  message: string;
}

/**
 * Hook for managing user timezone.
 * Automatically fetches and detects timezone on mount for authenticated users.
 */
export function useTimezone() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const store = useTimezoneStore();
  const { timezone, initialized, setTimezone, setInitialized, isLoading, setLoading } = store;

  // Fetch timezone from server
  const { data: timezoneData, isLoading: isFetching } = useQuery<TimezoneResponse>({
    queryKey: ['settings', 'timezone'],
    queryFn: async () => {
      const response = await apiFetch('/api/settings/timezone', { skipWorkspaceHeader: true });
      if (!response.ok) {
        throw new Error('Failed to fetch timezone');
      }
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Auto-detect timezone mutation
  const detectMutation = useMutation<TimezoneDetectResponse>({
    mutationFn: async () => {
      const response = await apiFetch('/api/settings/timezone/detect', {
        method: 'POST',
        skipWorkspaceHeader: true,
      });
      if (!response.ok) {
        throw new Error('Failed to detect timezone');
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.detected && data.timezone) {
        setTimezone(data.timezone);
        queryClient.invalidateQueries({ queryKey: ['settings', 'timezone'] });
      }
    },
  });

  // Update timezone mutation
  const updateMutation = useMutation<TimezoneResponse, Error, string | null>({
    mutationFn: async (newTimezone) => {
      setLoading(true);
      const response = await apiFetch('/api/settings/timezone', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: newTimezone }),
        skipWorkspaceHeader: true,
      });
      if (!response.ok) {
        throw new Error('Failed to update timezone');
      }
      return response.json();
    },
    onSuccess: (data) => {
      setTimezone(data.timezone);
      queryClient.invalidateQueries({ queryKey: ['settings', 'timezone'] });
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  // Initialize timezone when data is fetched
  useEffect(() => {
    if (timezoneData && !initialized) {
      setTimezone(timezoneData.timezone);
      setInitialized(true);

      // If no timezone is set, try to auto-detect (only once)
      if (!timezoneData.timezone && !detectMutation.isPending) {
        detectMutation.mutate();
      }
    }
    // Note: detectMutation is intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezoneData, initialized, setTimezone, setInitialized]);

  // Get effective timezone (user's preference or browser fallback)
  const effectiveTimezone = getEffectiveTimezone(timezone);

  // Get UTC offset for display
  const utcOffset = getTimezoneOffset(effectiveTimezone);

  // Update timezone handler
  const updateTimezone = useCallback(
    (newTimezone: string | null) => {
      updateMutation.mutate(newTimezone);
    },
    [updateMutation]
  );

  // Detect timezone handler (explicit call)
  const detectTimezone = useCallback(() => {
    detectMutation.mutate();
  }, [detectMutation]);

  return {
    // Current timezone (user's saved preference, may be null)
    timezone,

    // Effective timezone (preference or browser fallback)
    effectiveTimezone,

    // UTC offset string (e.g., "GMT-8")
    utcOffset,

    // Browser's timezone
    browserTimezone: getBrowserTimezone(),

    // Loading states
    isLoading: isFetching || isLoading || updateMutation.isPending || detectMutation.isPending,

    // Whether timezone has been initialized
    initialized,

    // Actions
    updateTimezone,
    detectTimezone,

    // Mutation states for UI feedback
    isUpdating: updateMutation.isPending,
    isDetecting: detectMutation.isPending,
    updateError: updateMutation.error,
  };
}
